<script setup lang="ts">
import { computed } from 'vue';

import { MoneyFormatter } from '@apps/main/new-desktop/core/components/formatters';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppStandardModal, AppTag } from '@libs/ui';
import { SemboDate, DateFormat } from '@libs/sembo-dates';

import type {
  FerryDetailsModalData,
  TransportTag,
} from './FerryDetailsModal.model';
import FerrySegment from './FerrySegment.vue';

interface FerryDetailsModalProps {
  ferryData: FerryDetailsModalData;
  imageHandler: (src: string) => void;
  pax: string;
}

interface FerryDetailsModalEmits {
  (event: 'close'): void;
}

const { t } = i18n.global;

const props = defineProps<FerryDetailsModalProps>();

const emit = defineEmits<FerryDetailsModalEmits>();

const routeText = computed(
  () =>
    //TODO: Missing country name
    `${props.ferryData.departureText} - ${props.ferryData.arrivalText}`
);

function getTransportTags(): TransportTag[] {
  const tags: TransportTag[] = [];
  if (props.ferryData) {
    const departureDate = new SemboDate(props.ferryData?.departureDate);
    const arrivalDate = new SemboDate(props.ferryData?.arrivalDate);
    const isTransportArrivingDifferentDay = !departureDate.isSameDay(
      arrivalDate.get()
    );
    if (isTransportArrivingDifferentDay) {
      const title = `${t('planner.this_ferry_arrives_on')} ${arrivalDate.format(
        DateFormat.DateMonth
      )}`;
      const transportTag: TransportTag = {
        type: 'transport',
        iconName: 'calendarOutlined',
        title,
      };
      tags.push(transportTag);
    }
  }
  return tags;
}
</script>

<template>
  <AppStandardModal
    class="ferry-details-modal"
    :close-button-aria-label="$t('a11y.close_panel')"
    @close="emit('close')"
  >
    <template #title>{{ $t('planner.transport.ferry') }}</template>
    <template #subtitle>
      {{ routeText }}
    </template>
    <template #body>
      <FerrySegment
        :ferry-data="props.ferryData"
        :image-handler="props.imageHandler"
      />
      <div class="tags-container">
        <AppTag
          v-for="(tag, index) in getTransportTags()"
          :key="index"
          type="warning"
          :icon="{ name: tag.iconName, size: '13' }"
        >
          <template #title>
            <span class="tag-text">
              {{ $t(tag.title) }}
            </span>
          </template>
        </AppTag>
      </div>
    </template>
    <template #summary>
      <div class="ferry-details-modal-footer">
        <MoneyFormatter
          :data="{
            price: props.ferryData.price.amount,
            currency: props.ferryData.price.currency,
            minPrice: undefined,
            font: 'font-label-l-bold',
            color: 'color-neutral-70',
          }"
        />
        <span class="summary-pax">
          {{ $t('planner.total_for') }} {{ props.pax }}
        </span>
      </div>
    </template>
  </AppStandardModal>
</template>

<style scoped lang="scss">
.ferry-details-modal-footer {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  @include font-label-xl-bold;

  .summary-pax {
    @include font-label-m-light;
    color: var(--color-neutral-60);
  }
}
.tags-container {
  margin-bottom: var(--spacing-32);
}
</style>
